import React, { useEffect, useState } from 'react';
import { fetchSayings, createSaying, fetchCategories, fetchLanguages, fetchTranslations } from '../api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';

function PublicSayingsPage() {
  const [sayings, setSayings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [form, setForm] = useState({ saying: '', category_id: '', language_id: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [filters, setFilters] = useState({ 
    category_id: '', 
    language_id: '',
    searchText: '',
    approved: true
  });
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    size: 15,
    total: 0,
    pages: 0
  });
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      setLoading(true);
      const [sayingsData, categoriesData, languagesData] = await Promise.all([
        fetchSayings({ 
          ...filters,
          translation_language_id: selectedLanguage || undefined
        }, pagination.page, pagination.size),
        fetchCategories(),
        fetchLanguages()
      ]);
      setSayings(sayingsData.items);
      setPagination({
        page: sayingsData.page,
        size: sayingsData.size,
        total: sayingsData.total,
        pages: sayingsData.pages
      });
      setCategories(categoriesData);
      setLanguages(languagesData);
      setError(null);

      // Load translations if a language is selected
      if (selectedLanguage) {
        try {
          const translationsData = await fetchTranslations({ 
            language_id: selectedLanguage
          });
          setTranslations(translationsData);
        } catch (err) {
          console.error('Failed to load translations:', err);
          setError('Error al cargar las traducciones.');
        }
      } else {
        setTranslations([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar los datos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters, pagination.page, pagination.size, selectedLanguage]);

  const handleLanguageChange = e => {
    const value = e.target.value;
    setSelectedLanguage(value);
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.saying || !form.category_id || !form.language_id) return;
    
    // Clear previous states
    setError(null);
    setSuccess(false);
    setShowSuccessDialog(false);
    
    try {
      await createSaying({
        saying: form.saying,
        category_id: Number(form.category_id),
        language_id: Number(form.language_id)
      });
      setSuccess(true);
      setShowSuccessDialog(true);
      setForm({ saying: '', category_id: '', language_id: '' });
      loadData();
    } catch (err) {
      console.error('Error creating saying:', err);
      setError(`Error al enviar el dicho: ${err.message}`);
    }
  };

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchInputChange = e => {
    setSearchInput(e.target.value);
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, searchText: searchInput }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setFilters({ category_id: '', language_id: '', searchText: '', approved: true });
    setSearchInput('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSayingClick = (sayingId) => {
    navigate(`/sayings/${sayingId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <h1 className="text-3xl font-bold text-center">Dichos de la Abuela</h1>
      <p className="text-center text-muted-foreground mb-4">Donde las palabras tienen historia y el corazón tiene voz</p>

      

      {/* Filter Controls */}
      <div className="bg-card p-6 rounded-lg shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Filtros</h3>
          <Button variant="outline" onClick={clearFilters}>
            Limpiar Filtros
          </Button>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-1 min-w-[200px] gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Buscar dichos..."
              className="flex-1 px-3 py-2 rounded-md border border-input bg-background"
            />
            <Button onClick={handleSearch}>
              Buscar
            </Button>
          </div>
          <select 
            name="category_id" 
            value={filters.category_id} 
            onChange={handleFilterChange}
            className="px-3 py-2 rounded-md border border-input bg-background min-w-[150px]"
          >
            <option value="">Todas las Categorías</option>
            {categories.map(c => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <select 
            name="language_id" 
            value={filters.language_id} 
            onChange={handleFilterChange}
            className="px-3 py-2 rounded-md border border-input bg-background min-w-[150px]"
          >
            <option value="">Todos los Idiomas</option>
            {languages.map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Mostrar traducciones en:</label>
          <select
            value={selectedLanguage}
            onChange={handleLanguageChange}
            className="px-3 py-2 rounded-md border border-input bg-background flex-1"
          >
            <option value="">Selecciona un idioma</option>
            {languages.filter(l => !l.is_default).map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-muted-foreground">
          Mostrando {sayings.length} de {pagination.total} dichos
        </div>
      </div>

     

      {/* Display Sayings */}
      <div className="space-y-4">
        {sayings.length === 0 ? (
          <p className="text-center text-muted-foreground">
            {selectedLanguage 
              ? "No se encontraron dichos con traducciones en el idioma seleccionado."
              : "No se encontraron dichos."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sayings.map((saying) => {
              const translation = translations.find(t => t.original_saying_id === saying.saying_id);
              return (
                <div
                  key={saying.saying_id}
                  className="bg-card text-card-foreground rounded-lg border p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => handleSayingClick(saying.saying_id)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSayingClick(saying.saying_id);
                    }
                  }}
                >
                  <div className="space-y-2">
                    <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">{saying.saying}</h3>
                    {translation && (
                      <div>
                        <p className="text-primary italic">{translation.translated_text}</p>
                        {translation.explanation && (
                          <p className="text-sm text-muted-foreground italic mt-1">
                            <strong>Explicación:</strong> {translation.explanation}
                          </p>
                        )}
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground">
                      <p>Categoría: {categories.find(c => c.category_id === saying.category_id)?.category_name}</p>
                      <p>Idioma: {languages.find(l => l.language_id === saying.language_id)?.name}</p>
                    </div>
                    <div className="flex items-center text-sm text-primary mt-2">
                      <span>Ver todas las traducciones</span>
                      <svg 
                        className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="min-w-[80px]"
        >
          Anterior
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1)
            .filter(page => {
              if (page === 1 || page === pagination.pages) return true;
              if (Math.abs(page - pagination.page) <= 1) return true;
              return false;
            })
            .map((page, index, array) => {
              if (index > 0 && page - array[index - 1] > 1) {
                return (
                  <React.Fragment key={`ellipsis-${page}`}>
                    <span className="px-2 text-muted-foreground">•••</span>
                    <Button
                      key={page}
                      variant={page === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className="min-w-[32px] h-8"
                    >
                      {page}
                    </Button>
                  </React.Fragment>
                );
              }
              return (
                <Button
                  key={page}
                  variant={page === pagination.page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className="min-w-[32px] h-8"
                >
                  {page}
                </Button>
              );
            })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.pages}
          className="min-w-[80px]"
        >
          Siguiente
        </Button>
      </div>
      <div className="space-y-4">Agregar nuevo dicho</div>
      <form onSubmit={handleSubmit} className="space-y-4 bg-card p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            name="saying"
            value={form.saying}
            onChange={handleChange}
            placeholder="Comparte un dicho..."
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          />
          <select
            name="category_id"
            value={form.category_id}
            onChange={handleChange}
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          >
            <option value="">Categoría</option>
            {categories.map(c => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <select
            name="language_id"
            value={form.language_id}
            onChange={handleChange}
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          >
            <option value="">Idioma</option>
            {languages.map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <Button type="submit" className="w-full md:w-auto">Enviar Dicho</Button>
        {error && (
          <div className="text-center text-destructive mt-2">{error}</div>
        )}
      </form>
      {/* Warning Message */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Advertencia / Warning</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p><strong>Español:</strong> Algunos de estos dichos contienen lenguaje fuerte, se recomienda discreción del espectador.</p>
              <p className="mt-1"><strong>English:</strong> Some of these sayings contain strong language, viewer discretion is advised.</p>
            </div>

            <div className="mt-2 text-sm text-yellow-700">
              <p><strong>Español:</strong> Las traducciones fueron hechas por un AI agente, por lo que pueden contener errores.</p>
              <p className="mt-1"><strong>English:</strong> The translations were made by an AI agent, so they may contain errors.</p>
            </div>
          </div>
        </div>
      </div>
      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¡Dicho Enviado!</DialogTitle>
            <DialogDescription>
              Tu dicho ha sido enviado y está pendiente de aprobación del administrador.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose onClick={() => setShowSuccessDialog(false)}>
              Entendido
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PublicSayingsPage; 